import { Component, Vue, Prop } from 'vue-property-decorator';
import { QuestionnaireDTO } from '@/models/QuestionnaireDTO';
import { LocalityDTO } from '@/models/LocalityDTO';
import QuestionnaireService from '@/services/questionnaire.service';
import LocalityAutoComplete from '@/components/inputs/LocalityAutoComplete/LocalityAutoComplete.vue';
import InputText from '@/components/inputs/InputText/InputText.vue';
import InputTel from '@/components/inputs/InputTel/InputTel.vue';
import InputEmail from '@/components/inputs/InputEmail/InputEmail.vue';
import InputCheckbox from '@/components/inputs/InputCheckbox/InputCheckbox.vue';
import InputTextarea from '@/components/inputs/InputTextarea/InputTextarea.vue';
import StepStatus from '@/components/StepStatus/StepStatus.vue';
import RenounceButton from '@/components/RenounceButton/RenounceButton.vue';

@Component({
  components: {
    LocalityAutoComplete,
    InputText,
    InputTel,
    InputEmail,
    InputTextarea,
    StepStatus,
    RenounceButton,
    InputCheckbox,
  },
})
export default class QuestAddress extends Vue {
  @Prop({ required: false, default: false }) public isRecap!: boolean;
  public partId: number | undefined;
  public questionnaire: QuestionnaireDTO = {} as QuestionnaireDTO;
  public updating?: boolean = false;
  public errorMessage: string = '';
  public error: boolean = false;
  public localityNotInList: boolean = false;
  public localityDefault: LocalityDTO = {
    locId: -10,
    locZipCode: '',
    locName: this.$i18n.t('questionnaire.address.localityOther'),
  } as LocalityDTO
  
  /**
   * Get an array of all the property names from the questionnaire data in lowercase
   */
  get instanceFields(): string[] {
    if (this.addressExists) {
      const fields: string[] = [];
      Object.keys(this.questionnaire.address).forEach(e => fields.push(e.toLowerCase()));
      return fields;
    }
    return [];
  }
  
  /**
   * Get the errors returned by the API corresponding to the questionnaire data properties
   */
  get errors(): string[] {
    if (this.$store.state.questionnaireValidationErrors) {
      const obj = this.$store.state.questionnaireValidationErrors;
      const errorFields: string[] = [];
      Object.keys(obj).forEach((e) => {
        if (this.instanceFields.includes(e.toLowerCase())) {
          errorFields.push(obj[e].errors);
        }
      });
      return errorFields;
    }

    return [];
  }

  protected mounted() {
    this.partId = Number(this.$route.params.partId);
    // this.fetchQuestionnaire(this.partId);
    this.questionnaire = this.$store.state.questionnaire;
    if (!this.questionnaire.address.locality && this.questionnaire.address.localityManual ||
      (this.questionnaire.address.locality !== null && this.questionnaire.address.locality?.locId === -10)) {
        this.onLocalityNotInList(true);
    }
  }

  /**
   * Fetch the questionnaire data with the service
   * @param partId The participant ID
   */
  protected fetchQuestionnaire(partId: number) {
    QuestionnaireService.fetchQuestionnaire(partId, 'address')
      .then((response) => {
        this.questionnaire = response as QuestionnaireDTO;
        this.$store.commit('setQuestionnaireAddress', response.address)
      });
  }

  protected async onSubmit(event: any) {
    const quest = this.toDto(this.questionnaire);
    await QuestionnaireService.updateQuestionnaire(quest, 'address')
      .then(() => {
        this.questionnaire.address.isValidated = true;
        this.$store.commit('setQuestionnaireAddress', this.questionnaire.address);
        if (event.submitter.id === 'btn-continue') {
          this.$router.push({
            name: 'insurance',
            params: {
              screening: this.$route.params.screening,
              partId: String(this.partId),
            },
          });
        } else {
          this.$router.push({
            name: 'overview',
            params: {
              screening: this.$route.params.screening,
              partId: String(this.partId),
            },
          });
        }
      });
  }

  protected toDto(quest: QuestionnaireDTO): QuestionnaireDTO {
    const questClone = JSON.parse(JSON.stringify(quest));

    // A locality was chosen in the dropdown, so clear manual data of the locality
    if (!this.localityNotInList && quest.address.locality !== null) {
      questClone.address.localityManual = null;

      if (questClone.address.locality.locId < 0) {
        questClone.address.locality = null;
      }
    }

    // "locality not in list was checked" so clear the locality object
    if (this.localityNotInList) {
      questClone.address.locality = null;
    }

    return questClone;
  }

  get addressExists() {
    return !!this.$store.state.questionnaire.address;
  }

  get isPhoneRequired() {
    if (!this.addressExists) {
      return false;
    }
    return !this.$store.state.questionnaire.address.mobilePhone && 
      !this.$store.state.questionnaire.address.professionalPhone;
  }

  get isMobilePhoneRequired() {
    if (!this.addressExists) {
      return false;
    }
    return !this.$store.state.questionnaire.address.phone && 
      !this.$store.state.questionnaire.address.professionalPhone;
  }

  get isProfPhoneRequired() {
    if (!this.addressExists) {
      return false;
    }
    return !this.$store.state.questionnaire.address.mobilePhone && 
      !this.$store.state.questionnaire.address.phone;
  }

    /**
   * If checked, clears the other locality data
   * @param checked True if the checkbox is checked, false otherwise
   */
  protected onLocalityNotInList(checked: boolean) {
    this.localityNotInList = checked;

    // reinitialize data about the locality
    this.questionnaire.address.locality = checked ?  this.localityDefault : null;
  }
}

